import { React, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse } from "@mui/material";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import HistoryIcon from "@mui/icons-material/History";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import HandshakeIcon from "../../assets/icons/HandshakeIcon";
import SidebarNetworkItem from "./SidebarNetworkItem/SidebarNetworkItem";
import SidebarLoadItem from "./SidebarLoadItem/SidebarLoadItem";
import SidebarUserSelect from "./SidebarUserSelect/SidebarUserSelect";
import { ActiveUserContext } from "../Dashboard/Dashboard";

export default function Sidebar({
  carrierNetwork,
  viewingUser,
  setViewingUser,
  sidebarWidth,
  setSidebarWidth,
  recentlyViewed,
  handleBookmark,
  handleTableAction
}) {
  const activeUser = useContext(ActiveUserContext);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [openRecentlyViewed, setOpenRecentlyViewed] = useState(true);
  const [openRelationships, setOpenRelationships] = useState(true);
  const [openLeads, setOpenLeads] = useState(true);
  const [relationships, setRelationships] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadTitle, setLeadTitle] = useState("MY LEADS");
  const [relationTitle, setRelationTitle] = useState("MY RELATIONSHIPS");
  const expansionArrowViewBox = "-2 2 20 20";

  const toggleSidebar = () => {
    setOpenSidebar(!openSidebar);

    if (openSidebar) {
      setOpenRecentlyViewed(false);
      setOpenRelationships(false);
      setOpenLeads(false);
      setSidebarWidth(50);
    } else {
      setSidebarWidth(280);
    }
  };

  const toggleRecentlyViewed = () => {
    setOpenRecentlyViewed(!openRecentlyViewed);
    if (!openSidebar) { toggleSidebar(); }
  };

  const toggleRelationships = () => {
    setOpenRelationships(!openRelationships);
    if (!openSidebar) { toggleSidebar(); }
  };

  const toggleLeads = () => {
    setOpenLeads(!openLeads);
    if (!openSidebar) { toggleSidebar(); }
  };

  const validCheckMark = (array) => {
    array.forEach((element, index) => {
      const obj = { ...element };
      const today = new Date();
      const lastActivityDate = new Date(element.lastActivityDate);

      if (element.lastActivityDate) {
        (today.getDate() === lastActivityDate.getDate()) ? obj.checkMark = true : obj.checkMark = false;
      }

      array[index] = obj;
    });
    return array;
  };

  const handleNewInteraction = (index, item) => {
    item.isNew = !item.isNew;

    if (item.pipelineStatus === "RELATN") {
      let updatedRelationships = [...relationships];
      updatedRelationships[index] = item;
      setRelationships(updatedRelationships);
    }
    else {
      let updatedLeads = [...leads];
      updatedLeads[index] = item;
      setLeads(updatedLeads);
    }
  };

  useEffect(() => {
    if (viewingUser !== activeUser?.userIdMcld) {
      setLeadTitle("LEADS");
      setRelationTitle("RELATIONSHIPS");
    } else {
      setLeadTitle("MY LEADS");
      setRelationTitle("MY RELATIONSHIPS");
    }
  }, [viewingUser]);

  useEffect(() => {
    const activities = validCheckMark(carrierNetwork);
    setRelationships(activities.filter(c => c.pipelineStatus === "RELATN"));
    setLeads(activities.filter(c => c.pipelineStatus === "LEAD"));
  }, [carrierNetwork]);

  return (
    <Drawer
      variant="permanent"
      open={openSidebar}
      sx={{ width: sidebarWidth, zIndex: "1220" }}
      PaperProps={{
        sx: {
          backgroundColor: "black_solid.80",
          width: sidebarWidth,
          overflowX: "hidden"
        }
      }}>
      <List disablePadding>
        {openSidebar && <SidebarUserSelect activeUser={activeUser} viewingUser={viewingUser} setViewingUser={setViewingUser} />}
        <ListItemButton
          onClick={toggleRelationships}
          sx={{
            backgroundColor: "black_solid.70",
            color: "black_solid.50"
          }}>
          <ListItemIcon>
            <HandshakeIcon sx={{ fontSize: "20px", color: "black_solid.50" }} />
          </ListItemIcon>
          <ListItemText primary={`${relationTitle} (${relationships.length})`} sx={{ color: "primary.30" }} />
          {openRelationships ? <ArrowDropUpIcon viewBox={expansionArrowViewBox} /> : <ArrowDropDownIcon viewBox={expansionArrowViewBox} />}
        </ListItemButton>
        <Collapse in={openRelationships}>
          <List disablePadding>
            {relationships.length > 0 ? relationships.map((r) => (
              <SidebarNetworkItem key={`relation-${r.carrierId}`} carrier={r} handleNewInteraction={handleNewInteraction} handleTableAction={handleTableAction} />
            )) : (
              <ListItem>
                <ListItemText primary="No relationships found" sx={{ color: "black_solid.50" }} />
              </ListItem>
            )}
          </List>
        </Collapse>
        <ListItemButton
          onClick={toggleLeads}
          sx={{
            backgroundColor: "black_solid.70",
            color: "black_solid.50"
          }}>
          <ListItemIcon>
            <RecentActorsIcon sx={{ fontSize: "20px", color: "black_solid.50" }} />
          </ListItemIcon>
          <ListItemText primary={`${leadTitle} (${leads.length})`} sx={{ color: "primary.30" }} />
          {openLeads ? <ArrowDropUpIcon viewBox={expansionArrowViewBox} /> : <ArrowDropDownIcon viewBox={expansionArrowViewBox} />}
        </ListItemButton>
        <Collapse in={openLeads}>
          <List disablePadding>
            {leads.length > 0 ? leads.map((r) => (
              <SidebarNetworkItem key={`lead-${r.carrierId}`} carrier={r} handleNewInteraction={handleNewInteraction} handleTableAction={handleTableAction} />
            )) : (
              <ListItem>
                <ListItemText primary="No leads found" sx={{ color: "black_solid.50" }} />
              </ListItem>
            )}
          </List>
        </Collapse>
        <ListItemButton
          onClick={toggleRecentlyViewed}
          sx={{
            backgroundColor: "black_solid.70",
            color: "black_solid.50"
          }}>
          <ListItemIcon>
            <HistoryIcon sx={{ fontSize: "20px", color: "black_solid.50" }} />
          </ListItemIcon>
          <ListItemText primary="RECENTLY VIEWED" sx={{ color: "primary.30" }} />
          {openRecentlyViewed ? <ArrowDropUpIcon viewBox={expansionArrowViewBox} /> : <ArrowDropDownIcon viewBox={expansionArrowViewBox} />}
        </ListItemButton>
        <Collapse in={openRecentlyViewed}>
          <List disablePadding>
            {recentlyViewed.length > 0 ? recentlyViewed.map((r, i) =>
              r.type === "carrier" ? (
                <SidebarNetworkItem key={`recent-item-${r.carrierId}`} carrier={r} index={i} handleBookmark={handleBookmark} handleTableAction={handleTableAction} isRecentlyItem />
              ) : (
                <SidebarLoadItem key={`recent-item-${r.loadId}`} load={r} index={i} handleBookmark={handleBookmark} handleTableAction={handleTableAction} />
              )) : (
              <ListItem>
                <ListItemText primary="No items found" sx={{ color: "black_solid.50" }} />
              </ListItem>
            )}
          </List>
        </Collapse>
      </List>
      <List sx={{ marginTop: "auto", backgroundColor: "black_solid.70", color: "black_solid.50" }} disablePadding>
        <ListItemButton onClick={toggleSidebar}>
          <DoubleArrowIcon
            sx={{
              fontSize: "20px",
              marginLeft: "auto",
              ...(openSidebar && {
                transform: "rotate(180deg)",
              })
            }}
          />
        </ListItemButton>
      </List>
    </Drawer>
  );
}

Sidebar.propTypes = {
  carrierNetwork: PropTypes.array.isRequired,
  viewingUser: PropTypes.string.isRequired,
  setViewingUser: PropTypes.func.isRequired,
  sidebarWidth: PropTypes.number.isRequired,
  setSidebarWidth: PropTypes.func.isRequired,
  recentlyViewed: PropTypes.array.isRequired,
  handleBookmark: PropTypes.func.isRequired,
  handleTableAction: PropTypes.func
};